import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Building2, Users, User, Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import { getStatusStyle } from './calendarUtils';

// Right-side drawer with centre session details, participants, staff, activities, generated shifts.
export function CalendarCentreDrawer({ sessionId, allShifts, open, onClose, onShiftClick }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    api.get(`/centre-sessions/${sessionId}/full`).then(res => {
      setDetails(res.data?.data || res.data || null);
    }).catch(() => setDetails(null)).finally(() => setLoading(false));
  }, [open, sessionId]);

  const sessionShifts = useMemo(() => {
    return allShifts.filter(s => s.session_id === sessionId).sort((a, b) => `${a.date} ${a.start_time}`.localeCompare(`${b.date} ${b.start_time}`));
  }, [allShifts, sessionId]);

  const session = details?.session;
  const participants = details?.participants || [];
  const staffList = details?.staff || [];
  const activities = details?.activities || [];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[460px] sm:w-[500px] overflow-y-auto" data-testid="centre-session-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Building2 className="w-4 h-4 text-amber-600" /> Centre Session Details</SheetTitle>
          <SheetDescription>Session info, participants, staff, activities, and generated shifts</SheetDescription>
        </SheetHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : session ? (
          <div className="space-y-4 mt-4">
            <Card>
              <CardContent className="p-3 space-y-2 text-sm">
                <div><p className="text-muted-foreground text-xs">Name</p><p className="font-medium">{session.name}</p></div>
                {session.description && <div><p className="text-muted-foreground text-xs">Description</p><p className="text-xs">{session.description}</p></div>}
                <div className="grid grid-cols-2 gap-2">
                  <div><p className="text-muted-foreground text-xs">Date</p><p className="font-medium">{session.session_date}</p></div>
                  <div><p className="text-muted-foreground text-xs">Time</p><p className="font-medium">{session.start_time} – {session.end_time}</p></div>
                </div>
                {session.location_name && <div><p className="text-muted-foreground text-xs">Location</p><p className="font-medium">{session.location_name}</p></div>}
                <Separator />
                <div className="grid grid-cols-3 gap-2">
                  <div><p className="text-muted-foreground text-xs">Status</p><Badge variant="outline" className="text-[10px]">{session.status}</Badge></div>
                  <div><p className="text-muted-foreground text-xs">Max</p><p className="font-medium">{session.max_participants}</p></div>
                  <div><p className="text-muted-foreground text-xs">Ratio</p><p className="font-medium">{session.default_ratio || '—'}</p></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Participants ({participants.length})</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5">
                {participants.length > 0 ? participants.map(p => (
                  <div key={p.id || p.participant_id} className="flex items-center justify-between bg-slate-50 rounded-md p-2">
                    <p className="text-xs font-medium">{p.participant_name || `Participant ${(p.participant_id || '').slice(-4)}`}</p>
                    <Badge variant="outline" className="text-[10px]">{p.status || 'BOOKED'}</Badge>
                  </div>
                )) : <p className="text-xs text-muted-foreground">No participants booked</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4" /> Staff ({staffList.length})</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5">
                {staffList.length > 0 ? staffList.map(s => (
                  <div key={s.id || s.staff_id} className="flex items-center justify-between bg-slate-50 rounded-md p-2">
                    <p className="text-xs font-medium">{s.staff_name || s.full_name || `Staff ${(s.staff_id || '').slice(-4)}`}</p>
                    <Badge variant="outline" className="text-[10px]">{s.role || s.staff_role || 'STAFF'}</Badge>
                  </div>
                )) : <p className="text-xs text-muted-foreground">No staff assigned</p>}
              </CardContent>
            </Card>

            {activities.length > 0 && (
              <Card>
                <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm">Activities ({activities.length})</CardTitle></CardHeader>
                <CardContent className="p-3 pt-0 space-y-1.5">
                  {activities.map(a => (
                    <div key={a.id} className="bg-slate-50 rounded-md p-2 text-xs">
                      <p className="font-medium">{a.name}</p>
                      <p className="text-[10px] text-muted-foreground">{a.start_time}–{a.end_time}{a.ratio ? ` · Ratio ${a.ratio}` : ''}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Generated Shifts ({sessionShifts.length})</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5 max-h-60 overflow-y-auto">
                {sessionShifts.length > 0 ? sessionShifts.map(s => {
                  const style = getStatusStyle(s.status);
                  return (
                    <div key={s.id} onClick={() => onShiftClick(s)}
                      className={`rounded-md border px-2 py-1.5 cursor-pointer hover:shadow-sm ${style.bg}`}
                      data-testid={`centre-shift-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-[11px] font-semibold ${style.text}`}>{s.start_time}–{s.end_time}</span>
                        <Badge variant="outline" className="text-[9px]">{s.status}</Badge>
                      </div>
                      <p className="text-[10px] text-slate-500 truncate">{s.assigned_staff?.map(a => a.name).join(', ') || 'Open'}</p>
                    </div>
                  );
                }) : <p className="text-xs text-muted-foreground">No shifts generated</p>}
              </CardContent>
            </Card>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mt-6">Session not found or centre sessions module is disabled</p>
        )}
      </SheetContent>
    </Sheet>
  );
}
